import type {
  GiftPassBundle,
  GiftPassBundleCreateRequest,
  GiftPassBundlePatchRequest,
  GiftPassBundlePreviewQuery,
  GiftPassPricingRule,
} from '@bonsai/shared';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';

import {
  createGiftPassBundle,
  fetchGiftPassBundle,
  fetchGiftPassBundlePreview,
  fetchGiftPassBundles,
  GIFT_PASS_BUNDLE_PAGE_LIMIT,
  GiftPassBundleApiError,
  updateGiftPassBundle,
} from '../../api/gift-pass-bundles';

export const GIFT_PASS_BUNDLES_KEY = ['gift-pass-bundles'] as const;
export const allGiftPassBundlesKey = () => [...GIFT_PASS_BUNDLES_KEY, 'list', 'all'] as const;
export const giftPassBundleKey = (id: string) => [...GIFT_PASS_BUNDLES_KEY, 'detail', id] as const;
export const giftPassBundlePreviewKey = (terms: GiftPassBundlePreviewQuery | null) =>
  [...GIFT_PASS_BUNDLES_KEY, 'preview', terms] as const;

/** A refusal, a bad query or broken settings (503) will not change on a second try. */
const retryServerErrorsOnce = (failures: number, error: Error) =>
  !(error instanceof GiftPassBundleApiError && (error.status < 500 || error.status === 503)) &&
  failures < 1;

export interface AllGiftPassBundles {
  bundles: GiftPassBundle[];
  pricing: GiftPassPricingRule | null;
}

/**
 * Follows the API's pages until it has every bundle, since the tabs and search
 * work on the whole set. Keyed by id because a bundle created between two page
 * requests shifts the offsets.
 */
export async function fetchAllGiftPassBundles(signal?: AbortSignal): Promise<AllGiftPassBundles> {
  const byId = new Map<string, GiftPassBundle>();
  let offset = 0;
  for (;;) {
    const page = await fetchGiftPassBundles({ limit: GIFT_PASS_BUNDLE_PAGE_LIMIT, offset }, signal);
    for (const bundle of page.bundles) byId.set(bundle.id, bundle);
    // Step by what the server returned, in case it caps pages below what was asked.
    offset += page.bundles.length;
    if (page.bundles.length === 0 || offset >= page.pagination.total) {
      return { bundles: [...byId.values()], pricing: page.pricing };
    }
  }
}

export function useAllGiftPassBundles() {
  return useQuery({
    queryKey: allGiftPassBundlesKey(),
    queryFn: ({ signal }) => fetchAllGiftPassBundles(signal),
    retry: retryServerErrorsOnce,
  });
}

export function useGiftPassBundle(id: string) {
  return useQuery({
    queryKey: giftPassBundleKey(id),
    queryFn: ({ signal }) => fetchGiftPassBundle(id, signal),
    enabled: id.length > 0,
    retry: retryServerErrorsOnce,
  });
}

/**
 * What the bundle would cost customers today, or nothing while the terms are
 * not valid yet. The last preview stays on screen while the next one loads.
 */
export function useGiftPassBundlePreview(terms: GiftPassBundlePreviewQuery | null) {
  return useQuery({
    queryKey: giftPassBundlePreviewKey(terms),
    queryFn: ({ signal }) =>
      fetchGiftPassBundlePreview(terms as GiftPassBundlePreviewQuery, signal),
    enabled: terms !== null,
    placeholderData: keepPreviousData,
    retry: retryServerErrorsOnce,
  });
}

function applySaved(qc: QueryClient, bundle: GiftPassBundle): void {
  qc.setQueryData(giftPassBundleKey(bundle.id), bundle);
  void qc.invalidateQueries({ queryKey: GIFT_PASS_BUNDLES_KEY });
}

export function useCreateGiftPassBundle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: GiftPassBundleCreateRequest) => createGiftPassBundle(body),
    onSuccess: (bundle) => applySaved(qc, bundle),
  });
}

export function useUpdateGiftPassBundle(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: GiftPassBundlePatchRequest) => updateGiftPassBundle(id, patch),
    onSuccess: (bundle) => applySaved(qc, bundle),
  });
}
